/**
 * AIF-01 — TanStack Query hooks for the taxonomy/v1 ai-finding-models surface
 * (12 endpoints): model list / detail / CRUD, version create/patch/delete
 * (keyed by `versionNumber`), class create/patch/delete (keyed by `classUuid`)
 * and bulk `classes/import`.
 *
 * Wiring mirrors `defect-catalogues/taxonomyQueries.ts`: the host `useApi()`
 * axios instance is base-pinned to `/api/lyse/v1`, so every request passes a
 * per-request `baseURL` override (`getTaxonomyBaseUrl()`) to resolve against
 * the taxonomy gateway while keeping the shared auth / tenant / refresh
 * interceptors. Conventions (staleTime 5m / gcTime 10m / retry 1 /
 * meta.failureToast) match the catalogue hooks. Mutations invalidate the
 * affected keys.
 *
 * SYNC: backend-requirement taxonomy-1.5.0-aif-01.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getTaxonomyBaseUrl } from "@aeriosiq/auth-client";
import type {
  AiFindingModel,
  AiFindingModelClass,
  AiFindingModelVersion,
  AiModelArtifactUploadTarget,
  CreateAiFindingModelBody,
  CreateAiFindingModelClassBody,
  CreateAiFindingModelVersionBody,
  GetAiFindingModelResponse,
  ImportAiModelClassesBody,
  ImportAiModelClassesResponse,
  ListAiFindingModelsResponse,
  UpdateAiFindingModelBody,
  UpdateAiFindingModelClassBody,
  UpdateAiFindingModelVersionBody,
} from "@aeriosiq/types";

const STALE_TIME_MS = 5 * 60_000;
const GC_TIME_MS = 10 * 60_000;

/** Per-request axios config that re-points the base at the taxonomy gateway. */
function tx(): { baseURL: string } {
  return { baseURL: getTaxonomyBaseUrl() };
}

const enc = encodeURIComponent;

export const aiModelKeys = {
  all: ["ai-models"] as const,
  list: ["ai-models", "list"] as const,
  detail: (modelId: string) => ["ai-models", "detail", modelId] as const,
};

// ── Reads ───────────────────────────────────────────────────────────────────

/** GET /ai-finding-models — list (incl. version/class counts + latest). */
export function useAiModelsQuery() {
  const api = useApi();
  return useQuery<ListAiFindingModelsResponse>({
    queryKey: aiModelKeys.list,
    queryFn: async () => {
      const res = await api.get<ListAiFindingModelsResponse>(
        "/ai-finding-models",
        tx(),
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "AI models unavailable" },
  });
}

/** GET /ai-finding-models/{modelId} — header + versions + classes. */
export function useAiModelQuery(modelId: string | null) {
  const api = useApi();
  return useQuery<GetAiFindingModelResponse>({
    queryKey: aiModelKeys.detail(modelId ?? ""),
    enabled: modelId !== null && modelId.length > 0,
    queryFn: async () => {
      const id = modelId ?? "";
      const res = await api.get<GetAiFindingModelResponse>(
        `/ai-finding-models/${enc(id)}`,
        tx(),
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "AI model unavailable" },
  });
}

// ── Model lifecycle mutations ────────────────────────────────────────────────

export function useCreateAiModelMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<AiFindingModel, Error, CreateAiFindingModelBody>({
    mutationFn: async (body) => {
      const res = await api.post<AiFindingModel>(
        "/ai-finding-models",
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

export function useUpdateAiModelMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<AiFindingModel, Error, UpdateAiFindingModelBody>({
    mutationFn: async (body) => {
      const res = await api.patch<AiFindingModel>(
        `/ai-finding-models/${enc(modelId)}`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

export function useDeleteAiModelMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (modelId: string): Promise<void> => {
      await api.delete(`/ai-finding-models/${enc(modelId)}`, tx());
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

// ── Version mutations (keyed by versionNumber) ───────────────────────────────

export function useCreateAiModelVersionMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    AiFindingModelVersion,
    Error,
    CreateAiFindingModelVersionBody
  >({
    mutationFn: async (body) => {
      const res = await api.post<AiFindingModelVersion>(
        `/ai-finding-models/${enc(modelId)}/versions`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

export function useUpdateAiModelVersionMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    AiFindingModelVersion,
    Error,
    { versionNumber: number; body: UpdateAiFindingModelVersionBody }
  >({
    mutationFn: async ({ versionNumber, body }) => {
      const res = await api.patch<AiFindingModelVersion>(
        `/ai-finding-models/${enc(modelId)}/versions/${String(versionNumber)}`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

export function useDeleteAiModelVersionMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (versionNumber: number): Promise<void> => {
      await api.delete(
        `/ai-finding-models/${enc(modelId)}/versions/${String(versionNumber)}`,
        tx(),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

/**
 * R0627-08 — mint a write-scoped Azure SAS to upload a version's weights file.
 * `POST /ai-finding-models/{modelId}/versions/{versionNumber}/artifact-upload-url`
 * (`mintAiModelArtifactUploadUrl`). Read-only mint — no cache invalidation; the
 * subsequent `artifactUri` PATCH (via `useUpdateAiModelVersionMutation`) is what
 * refreshes the version list.
 */
export function useMintAiModelArtifactUploadUrlMutation(modelId: string) {
  const api = useApi();
  return useMutation<AiModelArtifactUploadTarget, Error, number>({
    mutationFn: async (versionNumber) => {
      const res = await api.post<AiModelArtifactUploadTarget>(
        `/ai-finding-models/${enc(modelId)}/versions/${String(versionNumber)}/artifact-upload-url`,
        undefined,
        tx(),
      );
      return res.data;
    },
  });
}

// ── Class mutations (keyed by classUuid) ─────────────────────────────────────

export function useCreateAiModelClassMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<AiFindingModelClass, Error, CreateAiFindingModelClassBody>(
    {
      mutationFn: async (body) => {
        const res = await api.post<AiFindingModelClass>(
          `/ai-finding-models/${enc(modelId)}/classes`,
          body,
          tx(),
        );
        return res.data;
      },
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
        void qc.invalidateQueries({ queryKey: aiModelKeys.list });
      },
    },
  );
}

export function useUpdateAiModelClassMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    AiFindingModelClass,
    Error,
    { classUuid: string; body: UpdateAiFindingModelClassBody }
  >({
    mutationFn: async ({ classUuid, body }) => {
      const res = await api.patch<AiFindingModelClass>(
        `/ai-finding-models/${enc(modelId)}/classes/${enc(classUuid)}`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
    },
  });
}

export function useDeleteAiModelClassMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (classUuid: string): Promise<void> => {
      await api.delete(
        `/ai-finding-models/${enc(modelId)}/classes/${enc(classUuid)}`,
        tx(),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}

// ── Bulk class import ────────────────────────────────────────────────────────

/** POST /ai-finding-models/{modelId}/classes/import. */
export function useImportAiModelClassesMutation(modelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    ImportAiModelClassesResponse,
    Error,
    ImportAiModelClassesBody
  >({
    mutationFn: async (body) => {
      const res = await api.post<ImportAiModelClassesResponse>(
        `/ai-finding-models/${enc(modelId)}/classes/import`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiModelKeys.detail(modelId) });
      void qc.invalidateQueries({ queryKey: aiModelKeys.list });
    },
  });
}
